import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Checkbox,
} from '@roger/ui-components';
import {
  AlertCircle,
  Check,
  CheckCircle,
  Loader2,
  Paperclip,
  Trash2,
  Upload,
} from 'lucide-react';
import { DateField, DropdownField, Field } from '../FormFields';
import {
  deleteIntakeDocument,
  uploadDocumentToIntake,
} from '../../services/api';
import type {
  AgencyInsuranceResponse,
  AuthorizationStatus,
} from '../../services/api/types.gen';
import { useIntake } from '../../hooks/useIntake';
import { useIntakeFieldSave } from '../../hooks/useIntakeFieldSave';
import { useStore } from '../../store';
import {
  IntakeTabValidationKey,
  type IntakeRecord,
} from '../../store/slices/intakeSlice';
import { AUTHORIZATION_STATUS_OPTIONS } from '../patient/authorizationConstants';
import {
  AUTHORIZATION_VISIT_COUNT_FIELDS,
  type AuthorizationVisitCountKey,
  isAuthorizationDraftValid,
} from './authorizationDraftValidation';
import MarkCompletedButton from './MarkCompletedButton';

type AuthorizationSaveField =
  | AuthorizationVisitCountKey
  | 'authorization_status'
  | 'authorization_number'
  | 'authorization_notes'
  | 'authorization_request_date'
  | 'authorization_intake_document_id';

interface PriorAuthStepProps {
  selectedIntake: IntakeRecord | null;
  agencyInsurances: AgencyInsuranceResponse[];
  agencyInsurancesLoading?: boolean;
  onStatusChange?: () => void | Promise<void>;
}

type VisitCounts = Record<AuthorizationVisitCountKey, string>;

interface AuthorizationFormData {
  authorization_request_date: string | null;
  status: AuthorizationStatus;
  authorization_number: string;
  comment: string;
  intake_document_id: string | null;
  visit_counts: VisitCounts;
}

const EMPTY_VISIT_COUNTS: VisitCounts = {
  sn_visits: '',
  pt_visits: '',
  ot_visits: '',
  st_visits: '',
  msw_visits: '',
  hha_visits: '',
  dietician_visits: '',
  misc_visits: '',
};

function parseVisitCount(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isInteger(parsed) && parsed >= 0 ? parsed : null;
}

function isInvalidVisitCount(value: string): boolean {
  return value.trim() !== '' && parseVisitCount(value) === null;
}

function initialFormData(intake: IntakeRecord): AuthorizationFormData {
  return {
    authorization_request_date:
      typeof intake.authorization_request_date === 'string'
        ? intake.authorization_request_date.split('T')[0]
        : (intake.authorization_request_date?.toISOString().split('T')[0] ??
          null),
    status: intake.authorization_status ?? 'NOT_STARTED',
    authorization_number: intake.authorization_number ?? '',
    comment: intake.authorization_notes ?? '',
    intake_document_id: intake.authorization_intake_document_id ?? null,
    visit_counts: Object.fromEntries(
      AUTHORIZATION_VISIT_COUNT_FIELDS.map(({ key }) => [
        key,
        intake[key]?.toString() ?? EMPTY_VISIT_COUNTS[key],
      ])
    ) as VisitCounts,
  };
}

function toVisitCounts(
  formData: AuthorizationFormData
): Record<AuthorizationVisitCountKey, number | null> {
  return {
    sn_visits: parseVisitCount(formData.visit_counts.sn_visits),
    pt_visits: parseVisitCount(formData.visit_counts.pt_visits),
    ot_visits: parseVisitCount(formData.visit_counts.ot_visits),
    st_visits: parseVisitCount(formData.visit_counts.st_visits),
    msw_visits: parseVisitCount(formData.visit_counts.msw_visits),
    hha_visits: parseVisitCount(formData.visit_counts.hha_visits),
    dietician_visits: parseVisitCount(formData.visit_counts.dietician_visits),
    misc_visits: parseVisitCount(formData.visit_counts.misc_visits),
  };
}

function formHasInvalidVisitCounts(formData: AuthorizationFormData): boolean {
  return AUTHORIZATION_VISIT_COUNT_FIELDS.some(({ key }) =>
    isInvalidVisitCount(formData.visit_counts[key])
  );
}

const PriorAuthStep: React.FC<PriorAuthStepProps> = ({
  selectedIntake,
  agencyInsurances,
  agencyInsurancesLoading = false,
  onStatusChange,
}) => {
  const { updateIntakeRecord } = useIntake();
  const setIntakeTabValid = useStore(state => state.setIntakeTabValid);
  const [formData, setFormData] = useState<AuthorizationFormData | null>(null);
  const [hasEditedDraft, setHasEditedDraft] = useState(false);
  const [saveMessage, setSaveMessage] = useState<string | null>(null);
  const [attachmentFileName, setAttachmentFileName] = useState<string | null>(
    null
  );
  const [isAttachmentUploading, setIsAttachmentUploading] = useState(false);
  const [isAttachmentRemoving, setIsAttachmentRemoving] = useState(false);
  const [createOnOnboarding, setCreateOnOnboarding] = useState(true);
  const formDataRef = useRef<AuthorizationFormData | null>(null);
  const attachmentInputRef = useRef<HTMLInputElement | null>(null);
  const intakeIdRef = useRef<string | null>(null);
  const dirtyFieldsRef = useRef(new Set<AuthorizationSaveField>());
  // Last values known to be on the server for this intake. selectedIntake can lag
  // a successful PATCH (store update / parent rerender), so blur/remove compare
  // against this instead of the prop alone.
  const lastPersistedRef = useRef<
    Partial<Record<AuthorizationSaveField, string | number | boolean | null>>
  >({});
  const { saveStatus, saveField } = useIntakeFieldSave({
    intakeId: selectedIntake?.id,
    updateIntakeRecord,
  });

  // A response to an autosave replaces selectedIntake. Only initialize when the
  // user changes intakes so that an older response cannot clobber newer typing.
  useEffect(() => {
    if (!selectedIntake || intakeIdRef.current === selectedIntake.id) return;
    const initial = initialFormData(selectedIntake);
    intakeIdRef.current = selectedIntake.id;
    dirtyFieldsRef.current.clear();
    lastPersistedRef.current = {
      authorization_request_date: initial.authorization_request_date,
      authorization_status: initial.status,
      authorization_number: initial.authorization_number.trim() || null,
      authorization_notes: initial.comment.trim() || null,
      authorization_intake_document_id: initial.intake_document_id,
      ...Object.fromEntries(
        AUTHORIZATION_VISIT_COUNT_FIELDS.map(({ key }) => [
          key,
          selectedIntake[key] ?? null,
        ])
      ),
    };
    formDataRef.current = initial;
    setFormData(initial);
    setAttachmentFileName(
      selectedIntake.documents?.find(
        document => document.id === initial.intake_document_id
      )?.file_name ?? null
    );
    setIsAttachmentUploading(false);
    setIsAttachmentRemoving(false);
    setCreateOnOnboarding(
      selectedIntake.create_authorization_on_onboarding ?? true
    );
    setHasEditedDraft(
      AUTHORIZATION_VISIT_COUNT_FIELDS.some(
        ({ key }) => selectedIntake[key] != null
      ) ||
        Boolean(selectedIntake.authorization_number) ||
        Boolean(selectedIntake.authorization_notes) ||
        Boolean(selectedIntake.authorization_intake_document_id) ||
        Boolean(
          selectedIntake.authorization_status &&
          selectedIntake.authorization_status !== 'NOT_STARTED'
        )
    );
    setSaveMessage(null);
  }, [selectedIntake]);

  const isFormValid =
    formData !== null &&
    !formHasInvalidVisitCounts(formData) &&
    isAuthorizationDraftValid(toVisitCounts(formData));

  const agencyInsurance = selectedIntake?.agency_insurance_id
    ? agencyInsurances.find(
        insurance => insurance.id === selectedIntake.agency_insurance_id
      )
    : undefined;
  const isPriorAuthRequired =
    agencyInsurance?.visit_authorization_required ?? false;

  useEffect(() => {
    setIntakeTabValid(
      IntakeTabValidationKey.PriorAuth,
      !isPriorAuthRequired || !createOnOnboarding || isFormValid
    );
  }, [
    createOnOnboarding,
    hasEditedDraft,
    isFormValid,
    isPriorAuthRequired,
    setIntakeTabValid,
  ]);

  const handleCreateOnOnboardingChange = async (checked: boolean) => {
    const previous = createOnOnboarding;
    setCreateOnOnboarding(checked);
    setSaveMessage(null);
    const saved = await saveField(
      'create_authorization_on_onboarding',
      checked
    );
    if (!saved) {
      setCreateOnOnboarding(previous);
      setSaveMessage('Unable to save. Please try again.');
    }
  };

  const replaceForm = useCallback(
    (next: AuthorizationFormData, dirtyField?: AuthorizationSaveField) => {
      formDataRef.current = next;
      setFormData(next);
      if (dirtyField) {
        dirtyFieldsRef.current.add(dirtyField);
        setHasEditedDraft(true);
      }
    },
    []
  );

  const authorizationValuesMatch = (
    left: string | number | boolean | null | undefined,
    right: string | number | boolean | null
  ): boolean => {
    const normalize = (value: string | number | boolean | null | undefined) =>
      value === '' || value === undefined ? null : value;
    return normalize(left) === normalize(right);
  };

  const saveAuthorizationField = useCallback(
    async function saveAuthorizationField(
      field: AuthorizationSaveField,
      value: string | number | boolean | null
    ) {
      if (!selectedIntake || !dirtyFieldsRef.current.has(field)) return;
      // Match Eligibility: skip no-op saves against last known server values.
      if (authorizationValuesMatch(lastPersistedRef.current[field], value)) {
        dirtyFieldsRef.current.delete(field);
        return;
      }
      setSaveMessage(null);
      const saved = await saveField(field, value);
      // Only clear dirty after success so a failed blur can retry.
      if (saved) {
        lastPersistedRef.current[field] = value;
        dirtyFieldsRef.current.delete(field);
      }
    },
    [saveField, selectedIntake]
  );

  if (!selectedIntake) return null;

  const updateField = (
    dirtyField: AuthorizationSaveField,
    update: (current: AuthorizationFormData) => AuthorizationFormData
  ) => {
    const current = formDataRef.current;
    if (!current) return;
    replaceForm(update(current), dirtyField);
  };

  const handleRequestDateBlur = (value?: string | null) => {
    const current = formDataRef.current;
    if (!current) return;
    const next = {
      ...current,
      authorization_request_date:
        value === undefined ? current.authorization_request_date : value,
    };
    // DateField supplies the latest value on close before React necessarily renders.
    if (
      next.authorization_request_date !== current.authorization_request_date
    ) {
      replaceForm(next, 'authorization_request_date');
    }
    void saveAuthorizationField(
      'authorization_request_date',
      next.authorization_request_date
    );
  };

  const handleAttachmentChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    const current = formDataRef.current;
    const intakeId = selectedIntake.id;
    if (!file || !current || intakeIdRef.current !== intakeId) return;
    if (file.type !== 'application/pdf') {
      setSaveMessage('Choose a PDF attachment.');
      return;
    }
    if (
      formHasInvalidVisitCounts(current) ||
      !isAuthorizationDraftValid(toVisitCounts(current))
    ) {
      setSaveMessage(
        'Correct the highlighted visit counts before attaching a file.'
      );
      return;
    }
    setSaveMessage(null);
    setAttachmentFileName(file.name);
    setIsAttachmentUploading(true);
    try {
      const response = await uploadDocumentToIntake({
        path: { intake_id: intakeId },
        query: { display_name: file.name },
        body: { file },
      });
      if (!response.data) throw new Error('No document returned from upload');
      if (intakeIdRef.current !== intakeId) return;
      const latest = formDataRef.current;
      if (!latest) return;
      const next = { ...latest, intake_document_id: response.data.id };
      replaceForm(next, 'authorization_intake_document_id');
      // Await the link write directly so a failed PATCH cannot leave the form
      // showing an attachment that was never stored on the intake.
      try {
        await updateIntakeRecord(intakeId, {
          authorization_intake_document_id: response.data.id,
        });
        lastPersistedRef.current.authorization_intake_document_id =
          response.data.id;
        dirtyFieldsRef.current.delete('authorization_intake_document_id');
        if (intakeIdRef.current === intakeId) {
          setIsAttachmentUploading(false);
        }
      } catch (linkError) {
        console.error('Failed to link authorization attachment:', linkError);
        // Upload already created an IntakeDocument; remove it so a failed link
        // does not leave an orphaned PDF on the intake.
        try {
          await deleteIntakeDocument({
            path: { document_id: response.data.id },
            throwOnError: true,
          });
        } catch (cleanupError) {
          console.error(
            'Failed to delete unlinked authorization attachment:',
            cleanupError
          );
        }
        if (intakeIdRef.current !== intakeId) return;
        const rolledBack = {
          ...(formDataRef.current ?? latest),
          intake_document_id: null,
        };
        formDataRef.current = rolledBack;
        setFormData(rolledBack);
        dirtyFieldsRef.current.delete('authorization_intake_document_id');
        setAttachmentFileName(null);
        setIsAttachmentUploading(false);
        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
        setSaveMessage('Unable to link attachment. Please try again.');
      }
    } catch (error) {
      console.error('Failed to upload authorization attachment:', error);
      if (intakeIdRef.current === intakeId) {
        setAttachmentFileName(null);
        setIsAttachmentUploading(false);
        if (attachmentInputRef.current) attachmentInputRef.current.value = '';
        setSaveMessage('Unable to upload attachment. Please try again.');
      }
    }
  };

  const handleAttachmentRemove = async () => {
    const current = formDataRef.current;
    const documentId = current?.intake_document_id;
    const intakeId = selectedIntake.id;
    if (
      !current ||
      !documentId ||
      isAttachmentUploading ||
      isAttachmentRemoving ||
      intakeIdRef.current !== intakeId
    )
      return;

    setIsAttachmentRemoving(true);
    setSaveMessage(null);
    try {
      await deleteIntakeDocument({
        path: { document_id: documentId },
        throwOnError: true,
      });
      if (intakeIdRef.current !== intakeId) return;
    } catch (error) {
      console.error('Failed to delete authorization attachment:', error);
      if (intakeIdRef.current === intakeId) {
        setIsAttachmentRemoving(false);
        setSaveMessage('Unable to remove attachment. Please try again.');
      }
      return;
    }

    const next = {
      ...(formDataRef.current ?? current),
      intake_document_id: null,
    };
    setAttachmentFileName(null);
    setIsAttachmentRemoving(false);
    if (attachmentInputRef.current) attachmentInputRef.current.value = '';
    replaceForm(next, 'authorization_intake_document_id');
    void saveAuthorizationField('authorization_intake_document_id', null);
  };

  const displayedSaveStatus =
    isAttachmentUploading || isAttachmentRemoving
      ? 'saving'
      : saveMessage
        ? 'error'
        : saveStatus;

  return (
    <Card className='h-full flex flex-col'>
      <CardHeader className='border-b py-3 px-4'>
        <div className='flex items-center justify-between'>
          <div className='flex items-center gap-2 text-xs text-gray-700'>
            {displayedSaveStatus === 'saving' && (
              <Loader2 className='h-3 w-3 animate-spin' />
            )}
            {displayedSaveStatus === 'saved' && (
              <Check className='h-3 w-3 text-green-600' />
            )}
            {displayedSaveStatus === 'error' && (
              <AlertCircle className='h-3 w-3 text-red-600' />
            )}
            {displayedSaveStatus === 'saving' && <span>Saving...</span>}
            {displayedSaveStatus === 'saved' && (
              <span className='text-green-600'>Saved</span>
            )}
            {displayedSaveStatus === 'error' && (
              <span className='text-red-600'>
                {saveMessage ?? 'Save failed'}
              </span>
            )}
          </div>
          <MarkCompletedButton
            intakeId={selectedIntake.id}
            tabKey='prior_auth'
            isComplete={
              selectedIntake.tab_statuses?.prior_auth === 'complete' ||
              selectedIntake.tab_statuses?.prior_auth === 'complete_with_errors'
            }
            onStatusChange={onStatusChange}
          />
        </div>
      </CardHeader>
      <CardContent className='flex-1 overflow-y-auto p-4 pt-3'>
        {agencyInsurancesLoading ? (
          <div className='flex h-full items-center justify-center'>
            <Loader2 className='h-8 w-8 animate-spin' />
          </div>
        ) : (
          <div className='space-y-6'>
            <div className='border bg-gray-50 px-4 py-3'>
              <div className='mb-2 font-semibold'>Insurance Information</div>
              {agencyInsurance ? (
                <div className='space-y-1 text-sm'>
                  <div>
                    <span className='font-medium'>Insurance:</span>{' '}
                    {agencyInsurance.name}
                  </div>
                  <div>
                    <span className='font-medium'>Payer ID:</span>{' '}
                    {agencyInsurance.edi_payer_id}
                  </div>
                  <div>
                    <span className='font-medium'>Clearinghouse:</span>{' '}
                    {agencyInsurance.edi_clearinghouse}
                  </div>
                </div>
              ) : (
                <div className='text-sm text-orange-700'>
                  No agency insurance selected.
                </div>
              )}
            </div>
            {isPriorAuthRequired && formData ? (
              <div className='space-y-5'>
                <div className='rounded-lg border border-orange-200 bg-orange-50 p-4'>
                  <h3 className='font-semibold text-orange-900'>
                    Prior Authorization Required
                  </h3>
                  <p className='text-sm text-orange-800'>
                    Enter the payer authorization details below.
                  </p>
                </div>
                <label className='flex items-center gap-2 text-sm font-medium'>
                  <Checkbox
                    checked={createOnOnboarding}
                    checkboxSize='sm'
                    onCheckedChange={checked =>
                      void handleCreateOnOnboardingChange(checked === true)
                    }
                  />
                  Create authorization during onboarding
                </label>
                {createOnOnboarding && (
                  <form aria-label='Prior authorization' className='space-y-5'>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <DateField
                        label='Authorization Request Date'
                        htmlFor='authorization_request_date'
                        value={formData.authorization_request_date}
                        onChange={value =>
                          updateField(
                            'authorization_request_date',
                            current => ({
                              ...current,
                              authorization_request_date: value,
                            })
                          )
                        }
                        onBlur={handleRequestDateBlur}
                      />
                    </div>
                    <div className='grid grid-cols-1 gap-4 md:grid-cols-2'>
                      <DropdownField
                        label='Authorization Status'
                        htmlFor='authorization_status'
                        value={formData.status}
                        onChange={value => {
                          const next = {
                            ...formDataRef.current!,
                            status: value as AuthorizationStatus,
                          };
                          replaceForm(next, 'authorization_status');
                          void saveAuthorizationField(
                            'authorization_status',
                            next.status
                          );
                        }}
                        options={AUTHORIZATION_STATUS_OPTIONS}
                        variant='formSelect'
                      />
                      <Field
                        label='Authorization Number'
                        htmlFor='authorization_number'
                        value={formData.authorization_number}
                        onChange={value =>
                          updateField('authorization_number', current => ({
                            ...current,
                            authorization_number: value,
                          }))
                        }
                        onBlur={() => {
                          const current = formDataRef.current;
                          if (!current) return;
                          void saveAuthorizationField(
                            'authorization_number',
                            current.authorization_number.trim() || null
                          );
                        }}
                      />
                    </div>
                    <div>
                      <p className='mb-1.5 text-sm font-medium'>
                        Authorized Visits
                      </p>
                      <div className='grid grid-cols-1 gap-3 md:grid-cols-4'>
                        {AUTHORIZATION_VISIT_COUNT_FIELDS.map(
                          ({ key, label }) => (
                            <label
                              key={key}
                              className='text-xs text-muted-foreground'
                            >
                              {label}
                              <input
                                aria-label={label}
                                aria-invalid={Boolean(
                                  hasEditedDraft &&
                                  isInvalidVisitCount(
                                    formData.visit_counts[key]
                                  )
                                )}
                                className='mt-1 w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground'
                                min={0}
                                step={1}
                                type='number'
                                value={formData.visit_counts[key]}
                                onChange={event =>
                                  updateField(key, current => ({
                                    ...current,
                                    visit_counts: {
                                      ...current.visit_counts,
                                      [key]: event.target.value,
                                    },
                                  }))
                                }
                                onBlur={() => {
                                  const value =
                                    formDataRef.current?.visit_counts[key];
                                  if (
                                    value === undefined ||
                                    isInvalidVisitCount(value)
                                  ) {
                                    return;
                                  }
                                  void saveAuthorizationField(
                                    key,
                                    parseVisitCount(value)
                                  );
                                }}
                              />
                              {hasEditedDraft &&
                                isInvalidVisitCount(
                                  formData.visit_counts[key]
                                ) && (
                                  <span className='mt-1 block text-destructive'>
                                    Enter a whole number of visits (0 or more).
                                  </span>
                                )}
                            </label>
                          )
                        )}
                      </div>
                    </div>
                    <Field
                      label='Comment'
                      htmlFor='authorization_comment'
                      value={formData.comment}
                      onChange={value =>
                        updateField('authorization_notes', current => ({
                          ...current,
                          comment: value,
                        }))
                      }
                      onBlur={() => {
                        const current = formDataRef.current;
                        if (!current) return;
                        void saveAuthorizationField(
                          'authorization_notes',
                          current.comment.trim() || null
                        );
                      }}
                      multiline
                    />
                    <div>
                      <p className='mb-1.5 text-sm font-medium'>Attachment</p>
                      <input
                        ref={attachmentInputRef}
                        id='authorization_attachment'
                        aria-label='Authorization attachment'
                        className='hidden'
                        accept='application/pdf'
                        type='file'
                        onChange={event => void handleAttachmentChange(event)}
                      />
                      {attachmentFileName || formData.intake_document_id ? (
                        <div className='flex items-center gap-2 text-sm'>
                          <Paperclip className='h-4 w-4 text-muted-foreground' />
                          <span className='truncate'>
                            {attachmentFileName ?? 'Attached file'}
                          </span>
                          <span className='text-xs text-muted-foreground'>
                            {isAttachmentUploading
                              ? '(uploading…)'
                              : isAttachmentRemoving
                                ? '(removing…)'
                                : '(uploaded)'}
                          </span>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            aria-label='Remove attachment'
                            disabled={
                              isAttachmentUploading || isAttachmentRemoving
                            }
                            onClick={() => void handleAttachmentRemove()}
                          >
                            <Trash2 className='h-4 w-4' />
                          </Button>
                        </div>
                      ) : (
                        <Button
                          type='button'
                          variant='outline'
                          size='sm'
                          onClick={() => attachmentInputRef.current?.click()}
                        >
                          <Upload className='mr-1 h-3.5 w-3.5' />
                          Upload File
                        </Button>
                      )}
                    </div>
                  </form>
                )}
              </div>
            ) : agencyInsurance ? (
              <div className='rounded-lg border border-green-200 bg-green-50 p-6'>
                <div className='flex items-center justify-center'>
                  <CheckCircle className='mr-3 h-8 w-8 text-green-600' />
                  <div>
                    <h3 className='font-semibold text-green-900'>
                      No Prior Authorization Required
                    </h3>
                    <p className='text-sm text-green-800'>
                      The selected insurance does not require prior
                      authorization.
                    </p>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default PriorAuthStep;
